"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import moment from 'moment';
import Chart from "./Chart";
import CustomChart from "./CustomChart";
// import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { BarChart3 } from "lucide-react";
// Host ส่งชื่อ bucket ผ่าน prop `informaticsGs` (จาก controller.config); ค่าที่ import
// เป็นค่า fallback เมื่อ host ไม่ได้ส่งมา
import { ISCHART_INFORMATICS_GS } from "@/informatics/config";
import GaDashboard from "./GaDashboard";
import MelabReport from "./MelabReport";
import { defaultDatasetTab, getDatasetTabs } from "./dataset-tabs";
import "./ChartPreviewDisplay.css";

export default function ChartPreviewDisplay(props: any) {
  const [trigger, setTrigger] = useState(false);
  const [search, setSearch] = useState("");
  const imageRef = useRef<any>({});
  const containerRef = useRef<HTMLDivElement>(null);
  const imageUrl = useRef<any>({});
  const initialLoad = useRef(false);
  const [openAdmin, setOpenAdmin] = useState(false);
  const [pane, setPane] = useState(0);
  const [selectedPeriod, setSelectedPeriod] = useState<{ [key: string]: string }>({});
  const [customItems, setCustomItems] = useState<any[]>([]);
  const [filteredItems, setFilteredItems] = useState<any[]>([]);
  const [selectedFilters, setSelectedFilters] = useState<{ [key: string]: string }>({});
  const [filterOptions, setFilterOptions] = useState<{ year: string[], month: string[] }>({ year: [], month: [] });
  const [selectedDimensionFilters, setSelectedDimensionFilters] = useState<{ [key: string]: string }>({});
  const [dimensionOptions, setDimensionOptions] = useState<{ [key: string]: string[] }>({});
  const [selectedBreakdownYear, setSelectedBreakdownYear] = useState<string>("");
  const [breakdownYearOptions, setBreakdownYearOptions] = useState<string[]>([]);
  const [selectedDimYM, setSelectedDimYM] = useState<string>("");
  const [selectedDimY, setSelectedDimY] = useState<string>("");

  let { datasetId, selectedTab, showChart } = props;

  let isGaDataset = datasetId?.endsWith("_ga") ?? false;
  let displayPanes = datasetId ? ["Period", "Period Breakdown"].concat(selectedTab === "Recent" ? [] : ["Dimensions", "Custom", ...(isGaDataset ? ["Dashboard"] : [])]) : [];

  let Admin = props.Admin

  useEffect(() => {
    imageUrl.current = {};
  }, [props.datasetId]);

  const previewItems: any[] = useMemo(() => {
    if (!props.datasetId || !props.chartInfo) return [];
    const divisionId = props.selectedDivision?.[props.datasetId];
    const useDivision = divisionId && divisionId !== "All"
      && props.chartInfo[props.datasetId]?.divisionItems?.[divisionId]?.previewItems;
    if (useDivision) return props.chartInfo[props.datasetId].divisionItems[divisionId].previewItems;
    return props.chartInfo[props.datasetId]?.previewItems ?? [];
  }, [props.chartInfo, props.datasetId, props.selectedDivision]);

  const displayItems: any = useMemo(() => {
    if (!props.datasetId || !props.chartInfo) return null;
    const divisionId = props.selectedDivision?.[props.datasetId];
    const useDivision = divisionId && divisionId !== "All"
      && props.chartInfo[props.datasetId]?.divisionItems?.[divisionId]?.displayItems;
    if (useDivision) return props.chartInfo[props.datasetId].divisionItems[divisionId].displayItems;
    return props.chartInfo[props.datasetId]?.displayItems ?? null;
  }, [props.chartInfo, props.datasetId, props.selectedDivision]);

  useEffect(() => {
    updateImage();
  }, [displayItems, pane, search, selectedPeriod, filteredItems, selectedBreakdownYear]);

  useEffect(() => {
    setPane(0);
  }, [props.selectedTab]);

  useEffect(() => {
    setFilteredItems([]);
    setSelectedFilters({});
    setSelectedDimensionFilters({});
    setSelectedDimYM("");
    setSelectedDimY("");
    if (props.datasetId && props.chartInfo?.[props.datasetId]?.customItems) {
      let customItems = props.chartInfo[props.datasetId].customItems;
      const divisionId = getPrefix(props.datasetId) === "" ? null
        : props.selectedDivision?.[props.datasetId];

      if (
        divisionId &&
        props.chartInfo[props.datasetId]?.divisionItems?.[divisionId]?.customItems
      ) {
        customItems = props.chartInfo[props.datasetId].divisionItems[divisionId].customItems;
      }

      setCustomItems(customItems);

      const yearSet = new Set<string>();
      const monthSet = new Set<string>();
      customItems.forEach((item: any) => {
        if (item.filter?.y) yearSet.add(item.filter.y);
        if (item.filter?.ym) {
          const [year, month] = item.filter.ym.split('-');
          yearSet.add(year);
          monthSet.add(month);
        }
      });
      const yearOptions = Array.from(yearSet).sort();
      const monthOptions = Array.from(monthSet).sort();
      setFilterOptions({ year: yearOptions, month: monthOptions });
      const dimensionSets: { [key: string]: Set<string> } = {};
      customItems.forEach((item: any) => {
        Object.keys(item.filter || {}).forEach(key => {
          if (key !== 'y' && key !== 'ym') {
            if (!dimensionSets[key]) dimensionSets[key] = new Set();
            dimensionSets[key].add(item.filter[key]);
          }
        });
      });
      const newDimensionOptions: { [key: string]: string[] } = {};
      Object.keys(dimensionSets).forEach(key => {
        newDimensionOptions[key] = Array.from(dimensionSets[key]).sort();
      });
      setDimensionOptions(newDimensionOptions);
      const initialDimensionFilters = Object.keys(newDimensionOptions).reduce((acc, key) => ({ ...acc, [key]: "" }), {});
      setSelectedDimensionFilters(initialDimensionFilters);
      if (yearOptions.length > 0) {
        const latestYear = Math.max(...yearOptions.map(Number)).toString();
        const initialFilters = { year: latestYear, month: "" }
        setSelectedFilters(initialFilters);
        filterItems(customItems, initialFilters, initialDimensionFilters);
      }
    }
  }, [props.chartInfo, props.datasetId, props.selectedDivision]);

  useEffect(() => {
    const selectedDisplayItem = displayItems?.[selectedTab];
    if (selectedDisplayItem) {
      const years = new Set<string>();
      const extractYears = (source: any) => {
        Object.values(source || {}).forEach((items: any) => {
          items.forEach((item: any) => {
            if (Object.keys(item?.filter || {}).length > 1) {
              if (item.filter?.y) years.add(item.filter.y);
              if (item.filter?.ym) years.add(item.filter.ym.split('-')[0]);
            }
          });
        });
      };
      extractYears(selectedDisplayItem.onefilter);
      extractYears(selectedDisplayItem.twofilter);

      const sortedYears = Array.from(years).sort().reverse();
      setBreakdownYearOptions(sortedYears);
      if (sortedYears.length > 0) {
        if (!selectedBreakdownYear || !sortedYears.includes(selectedBreakdownYear)) {
          setSelectedBreakdownYear(sortedYears[0]);
        }
      } else {
        setSelectedBreakdownYear("");
      }
    }
  }, [displayItems, selectedTab]);

  const isVisible = (image: HTMLImageElement, container: HTMLDivElement) => {
    let { top, bottom, left, right } = container.getBoundingClientRect();
    let imageRect = image?.getBoundingClientRect();
    if (!imageRect) return false;
    let threshold = 20;
    return (
      (imageRect.top > top && imageRect.top < bottom)
      || (imageRect.bottom > top && imageRect.bottom < bottom)
      || (imageRect.left > left && imageRect.left < right)
      || (imageRect.right > left && imageRect.right < right)
      || (Math.abs(imageRect.top - bottom) < threshold)
      || (Math.abs(imageRect.top - top) < threshold)
      || (Math.abs(imageRect.bottom - top) < threshold)
      || (Math.abs(imageRect.bottom - bottom) < threshold)
      || (Math.abs(imageRect.left - right) < threshold)
      || (Math.abs(imageRect.left - left) < threshold)
      || (Math.abs(imageRect.right - left) < threshold)
      || (Math.abs(imageRect.right - right) < threshold)
    );
  }

  const getPrefix = (datasetId: string) => {
    const divisionId = props.selectedDivision?.[datasetId];
    if (!divisionId || divisionId === "All") {
      return ""
    } else {
      return `/${divisionId}`
    }
  }

  const toUrl = (clickItem: string) =>
    props.useCloud
      ? (`${props.informaticsGs ?? ISCHART_INFORMATICS_GS}/${props.datasetId}`
        + getPrefix(props.datasetId) + `/thumbnails/${clickItem}.png`)
      : (`${props.urlPrefix}data/${props.datasetId}`
        + getPrefix(props.datasetId) + `/thumbnails/${clickItem}.png`)

  const updateImage = () => {
    let urlList = [];
    for (var i in imageRef.current) {
      if (isVisible(imageRef.current?.[i], containerRef.current!)
        && !(Object.keys(imageUrl.current).includes(toUrl(previewItems?.[Number(i)]?.filename)))
      ) {
        urlList.push(toUrl(previewItems?.[Number(i)]?.filename));
      }
    }
    if (urlList.length > 0) {
      console.log('urlList ', imageRef, urlList, containerRef, previewItems, props.chartInfo, props.datasetId);
      getDownloadUrlList(urlList);
    } else {
      setTrigger(!trigger);
    }
  }

  const getDownloadUrlList = async (urlList: string[]) => {
    let downloadUrlList = await Promise.all(
      urlList
        .map(async (originalUrl: string) => {
          imageUrl.current[originalUrl] = "";
          if (props.useCloud) {
            return await props.toDownloadUrl(originalUrl);
          }
          return originalUrl;
        })
    );
    for (var i = 0; i < urlList.length; i++) {
      imageUrl.current[urlList[i]] = downloadUrlList[i];
    }
    setTrigger(!trigger);
  }

  const filterItems = (items: any[], filters: any, dimensionFilters: any) => {
    const filteredItems = items.filter(item => {
      const hasYear = !!filters.year;
      const hasMonth = !!filters.month;

      let matches = true;

      if (hasYear && !hasMonth) {
        matches = item.filter?.y === filters.year;
      } else if (hasYear && hasMonth) {
        matches =
          item.filter?.ym?.startsWith(filters.year) &&
          item.filter?.ym?.endsWith(filters.month);
      }

      Object.keys(dimensionFilters).forEach(dim => {
        const filterValue = dimensionFilters[dim];
        if (filterValue === "") {
          if (item.filter?.[dim] !== undefined && item.filter[dim] !== "") {
            matches = false;
          }
        } else {
          if (item.filter?.[dim] === undefined || item.filter[dim] !== filterValue) {
            matches = false;
          }
        }
      });

      return matches;
    });
    setFilteredItems(filteredItems);
  }

  const selectedDisplayItem = displayItems?.[selectedTab];
  const tabs = getDatasetTabs(datasetId, props.chartInfo);
  // แท็บ custom ที่ pipeline ประกาศผ่าน chartinfo.reportTab (เช่น melab_group แท็บ "report")
  // — UI ไม่เทียบชื่อ dataset ตามกฎใน dataset-tabs.ts
  const reportTab = props.chartInfo?.[datasetId]?.reportTab;
  const isReportTab = !!reportTab && selectedTab === reportTab;
  // console.log('Debug selectedDisplayItem: ', selectedDisplayItem);

  return (
    <div id="ChartPreviewDisplay" className="cpd-wrapper">
      <div>
        <div className="cpd-header">
          {/* Dataset select — choose dataset */}
          {props.showSelectorDropdown && props.chartInfo && Object.keys(props.chartInfo).length > 0 && (
            <select
              className="cpd-select"
              value={datasetId}
              onChange={(e) => {
                const chosen = e.target.value;
                props.setState({
                  datasetId: chosen,
                  selectedTab: defaultDatasetTab(chosen, props.chartInfo),
                });
              }}
              style={{ flexShrink: 0 }}
            >
              {Object.keys(props.chartInfo).map((id: string) => (
                <option key={id} value={id}>{id}</option>
              ))}
            </select>
          )}

          {/* Dataset tabs (Recent, Unique ID, ...) inline in header */}
          {props.showSelectorDropdown && datasetId && props.chartInfo?.[datasetId]?.displayItems && (
            <div className="cpd-tabs-inline">
              {tabs.map((tab: string, index: number) => (
                <div
                  key={index}
                  onClick={() => props.setState({ selectedTab: tab, datasetId })}
                  className={`cpd-tab ${selectedTab === tab ? "active" : ""}`}
                >
                  {tab}
                </div>
              ))}
            </div>
          )}

          {/* Search */}
          <div className="cpd-search-container">
            <input
              placeholder="Search..."
              className="cpd-search-input"
              value={search}
              onChange={(e: any) => setSearch(e.target.value)}
            />
            {search && (
              <button
                onClick={() => setSearch("")}
                className="cpd-search-clear"
              >
                ✕
              </button>
            )}
            {search && (
              <div className="cpd-search-dropdown">
                {(previewItems || [])
                  .map((item: any, index: number) => ({
                    originalIndex: index,
                    parts: item.filename.split("."),
                    agg: item.filename.split(".")?.[1],
                    dimCount: item.filename.split(".")?.[0].split("__").length,
                    ...item
                  }))
                  .filter((item: any) => (
                    (item.filter?.y && item.filter.y === moment().format('YYYY')) ||
                    (item.filter?.ym && item.filter.ym === moment().format('YYYY-MM'))))
                  .filter((item: any) => (
                    search ? item.filename.toLowerCase().includes(search.toLowerCase())
                      : true
                  ))
                  .map((item: any, index: number) => (
                    <div
                      key={index}
                      onClick={() => setSearch(item.filename)}
                      className="cpd-search-item"
                    >
                      {item.filename.split(".")?.[2]}
                    </div>
                  ))}
              </div>
            )}
          </div>

          {/* Right actions */}
          <div className="cpd-actions">
            {datasetId && Admin && (
              <button
                className="cpd-btn cpd-btn-outline"
                onClick={() => setOpenAdmin(true)}
              >
                Admin
              </button>
            )}
            {datasetId && (
              <button
                className="cpd-btn cpd-btn-outline"
                onClick={() => updateImage()}
              >
                Refresh
              </button>
            )}
          </div>
        </div>
        <div
          ref={containerRef}
          className="cpd-main-container"
          onScroll={updateImage}>
          {/* tabs moved to header */}
          {isReportTab ?
            <div className="cpd-scroll-area">
              <MelabReport
                datasetId={datasetId}
                onEvent={props.onEvent}
                duckLoaded={props.duckLoaded}
              />
            </div>
            : selectedDisplayItem ?

            <div className="cpd-scroll-area" onScroll={updateImage}>
              {pane === 0 &&
                <>
                  {(() => {
                    const groupByType = (entries: [string, any[]][], filterFn: (item: any) => boolean) => {
                      const grouped: { [type: string]: any[] } = {};
                      entries.forEach(([key, items]) => {
                        const type = key.split("=")[0];
                        const filtered = items.filter(filterFn)
                          .filter((item: any) => search ? item.filename.toLowerCase().includes(search.toLocaleLowerCase()) : true);
                        if (!grouped[type]) grouped[type] = [];
                        grouped[type].push(...filtered);
                      });
                      return grouped;
                    };
                    const ofGrouped = groupByType(
                      Object.entries(selectedDisplayItem?.onefilter || {}),
                      (item: any) => Object.keys(item?.filter)?.length == 1
                    );
                    const tfGrouped = groupByType(
                      Object.entries(selectedDisplayItem?.twofilter || {}),
                      (item: any) => Object.keys(item?.filter)?.length === 1
                        && ["y", "ym", "ymd"].includes(item?.parts?.[0]?.split("__")?.[0])
                    );
                    const renderGroup = (type: string, items: any[], prefix: string) => {
                      // console.log('Debug renderGroup items: ', items);
                      if (items.length === 0) return null;
                      const label = type === "ymd" ? "Date" : type === "ym" ? "Year-Month" : type === "y" ? "Year" : type;
                      const values = [...new Set(items.map((item: any) => item.filename.split(".")?.[2]))].sort() as string[];
                      const stateKey = `${prefix}_${type}`;
                      const selected = selectedPeriod[stateKey] || values[values.length - 1];
                      return (
                        <div key={stateKey} style={{ marginBottom: "12px" }}>
                          <div className="cpd-group-header">
                            <span className="cpd-group-label">{label}</span>
                            <select className="cpd-select" value={selected}
                              onChange={(e) => {

                                console.log('stateKey: ', stateKey);
                                console.log('e.target.value: ', e.target.value);
                                let v = e.target.value
                                setSelectedPeriod(prev => ({ ...prev, [stateKey]: v }))
                              }

                              }>
                              {values.map((v: string) => (
                                <option key={v} value={v}>{v.split("__")?.slice(1).join("__") || v}</option>
                              ))}
                            </select>
                          </div>
                          <div className="cpd-flex-wrap" onScroll={updateImage}>
                            {items
                              .filter((item: any) => item.filename.split(".")?.[2] === selected)
                              .sort((a: any, b: any) => a.filename < b.filename ? -1 : 1)
                              .map((item: any, itemIndex: number) => (
                                <PreviewPic
                                  key={itemIndex}
                                  setState={props.setState}
                                  selectedTab={selectedTab}
                                  datasetId={props.datasetId}
                                  previewItem={item}
                                  initialLoad={initialLoad}
                                  updateImage={updateImage}
                                  imageUrl={imageUrl}
                                  imageRef={imageRef}
                                  index={item.originalIndex}
                                  toUrl={toUrl}
                                  urlPrefix={props.urlPrefix}
                                />
                              ))}
                          </div>
                        </div>
                      );
                    };
                    return (<>
                      <div className="cpd-section">
                        <div className="cpd-section-header">
                          <h3 className="cpd-title">One Dimension / filters</h3>
                          <p className="cpd-subtitle">Charts showing data breakdown by a single category (e.g. Department, Gender, Age Group).</p>
                        </div>
                        {Object.entries(ofGrouped).sort((a, b) => b[0].localeCompare(a[0])).map(([type, items]) => renderGroup(type, items, "of"))}
                      </div>

                      <div className="cpd-section">
                        <div className="cpd-section-header">
                          <h3 className="cpd-title">Time-Based Analysis</h3>
                          <p className="cpd-subtitle">Dimensions: Year, or Year-Month (e.g. 2024, 2024-01)</p>
                        </div>
                        {Object.entries(tfGrouped).sort((a, b) => b[0].localeCompare(a[0])).map(([type, items]) => renderGroup(type, items, "tf"))}
                      </div>
                    </>);
                  })()}
                  <div className="cpd-flex-wrap">
                    <div className="w-full mb-4 mt-8">
                      <h3 className="cpd-title">Overall Period Trends</h3>
                      <p className="cpd-subtitle">Charts showing trends over time (Year, or Month) without filters (e.g. Total Patients per Year, Revenue per Month)</p>
                    </div>
                    {(selectedDisplayItem?.one || [])
                      .filter((item: any) => ["y", "ym", "ymd"].includes(item?.parts?.[0]?.split('__')?.[0]))
                      .filter((item: any) => (
                        search ? item.filename.toLowerCase().includes(search.toLocaleLowerCase())
                          : true
                      ))
                      .map((item: any, childIndex: number) => (
                        <PreviewPic
                          key={childIndex}
                          setState={props.setState}
                          selectedTab={selectedTab}
                          datasetId={props.datasetId}
                          previewItem={item}
                          initialLoad={initialLoad}
                          updateImage={updateImage}
                          imageUrl={imageUrl}
                          imageRef={imageRef}
                          index={item.originalIndex}
                          toUrl={toUrl}
                          urlPrefix={props.urlPrefix}
                        />
                      ))}
                  </div>
                  <div className="cpd-flex-wrap">
                    {(selectedDisplayItem.two || [])
                      .filter((item: any) => ["y", "ym", "ymd"].includes(item?.parts?.[0]?.split("__")?.[0]))
                      .filter((item: any) => (
                        search ? item.filename.toLowerCase().includes(search.toLocaleLowerCase())
                          : true
                      ))
                      .map((item: any, childIndex: number) => (
                        <PreviewPic
                          key={childIndex}
                          setState={props.setState}
                          selectedTab={selectedTab}
                          datasetId={props.datasetId}
                          previewItem={item}
                          initialLoad={initialLoad}
                          updateImage={updateImage}
                          imageUrl={imageUrl}
                          imageRef={imageRef}
                          index={item.originalIndex}
                          toUrl={toUrl}
                          urlPrefix={props.urlPrefix}
                        />
                      ))}
                  </div>
                </>}
              {pane === 1 &&  //----------------------------Period Brekadown pane -------------------
                <>
                  <div className="cpd-filter-bar" style={{
                    marginBottom: "1rem",
                    position: "sticky",
                    top: 0,
                    zIndex: 10,
                    backgroundColor: "white",
                    paddingTop: "10px",
                    paddingBottom: "10px",
                    borderBottom: "1px solid #eee"
                  }}>
                    <span style={{ marginRight: "0.5rem", fontWeight: 500 }}>Filter by Year:</span>
                    <select
                      className="cpd-select"
                      value={selectedBreakdownYear}
                      onChange={(e) => setSelectedBreakdownYear(e.target.value)}
                    >
                      {breakdownYearOptions.map(year => (
                        <option key={year} value={year}>{year}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    {Object.entries(selectedDisplayItem?.onefilter || {})
                      .sort((a: any, b: any) => a[0] < b[0] ? 1 : -1)
                      .map((entry: any, entryIndex: number) => ([
                        entry[0],
                        entry[1]
                          .filter((item: any) => Object.keys(item?.filter)?.length > 1)
                          .filter((item: any) => {
                            if (!selectedBreakdownYear) return true;
                            const y = item.filter?.y || item.filter?.ym?.split('-')[0];
                            return y === selectedBreakdownYear;
                          })
                          .filter((item: any) => (
                            search ? item.filename.toLowerCase().includes(search.toLocaleLowerCase())
                              : true
                          ))]
                      ))
                      .filter((entry: any) => entry[1].length > 0)
                      .sort((a: any, b: any) => {
                        let aFilter: any = a[0].split(" by ");
                        let bFilter: any = b[0].split(" by ");
                        if (aFilter[0] < bFilter[0])
                          return 1
                        else if (aFilter[0] > bFilter[0])
                          return -1
                        else if (aFilter[1] < bFilter[1])
                          return -1
                        else
                          return 1
                      })
                      .map((entry: any, entryIndex: number) => {
                        return (
                          <div key={entryIndex}>
                            <div className="cpd-group-header">
                              {entry[0].includes(" by ")
                                ? `By ${entry[0].split(" by ")[1].replace(/\b\w/g, (l: string) => l.toUpperCase())}`
                                : entry[0]}
                            </div>
                            <div className="cpd-horizontal-scroll" onScroll={updateImage}>
                              {entry[1]
                                .sort((a: any, b: any) =>
                                  a.filename.split(".")?.[2]?.split("__")?.[1] < b.filename.split(".")?.[2]?.split("__")?.[1] ? -1 : 1)
                                .map((item: any, itemIndex: number) => (
                                  <PreviewPic
                                    key={itemIndex}
                                    setState={props.setState}
                                    selectedTab={selectedTab}
                                    datasetId={props.datasetId}
                                    previewItem={item}
                                    initialLoad={initialLoad}
                                    updateImage={updateImage}
                                    imageUrl={imageUrl}
                                    imageRef={imageRef}
                                    index={item.originalIndex}
                                    toUrl={toUrl}
                                    urlPrefix={props.urlPrefix}
                                  />
                                ))}
                            </div>
                          </div>
                        )
                      })}
                  </div>
                  <div>
                    {Object.entries(selectedDisplayItem?.twofilter || {})
                      .sort((a: any, b: any) => a[0] < b[0] ? 1 : -1)
                      .map((entry: any, entryIndex: number) => ([
                        entry[0],
                        entry[1]
                          .filter((item: any) => Object.keys(item?.filter)?.length > 1)
                          .filter((item: any) => {
                            if (!selectedBreakdownYear) return true;
                            const y = item.filter?.y || item.filter?.ym?.split('-')[0];
                            return y === selectedBreakdownYear;
                          })
                          .filter((item: any) => (
                            search ? item.filename.toLowerCase().includes(search.toLocaleLowerCase())
                              : true))
                      ]))
                      .filter((entry: any) => entry[1].length > 0)
                      .sort((a: any, b: any) => {
                        let aFilter: any = a[0].split(" by ");
                        let bFilter: any = b[0].split(" by ");
                        if (aFilter[0] < bFilter[0])
                          return 1
                        else if (aFilter[0] > bFilter[0])
                          return -1
                        else if (aFilter[1] < bFilter[1])
                          return -1
                        else
                          return 1
                      })
                      .map((entry: any, entryIndex: number) => {
                        return (
                          <div key={entryIndex}>
                            <div className="cpd-group-header">
                              {entry[0].includes(" by ")
                                ? `By ${entry[0].split(" by ")[1].replace(/\b\w/g, (l: string) => l.toUpperCase())}`
                                : entry[0]}
                            </div>
                            <div className="cpd-horizontal-scroll" onScroll={updateImage}>
                              {entry[1]
                                .sort((a: any, b: any) =>
                                  a.filename.split(".")?.[2] < b.filename.split(".")?.[2] ? -1 : 1)
                                .map((item: any, itemIndex: number) => {
                                  return (
                                    <PreviewPic
                                      key={itemIndex}
                                      setState={props.setState}
                                      selectedTab={selectedTab}
                                      datasetId={props.datasetId}
                                      previewItem={item}
                                      initialLoad={initialLoad}
                                      updateImage={updateImage}
                                      imageUrl={imageUrl}
                                      imageRef={imageRef}
                                      index={item.originalIndex}
                                      toUrl={toUrl}
                                      urlPrefix={props.urlPrefix}
                                    />
                                  )
                                })}
                            </div>
                          </div>
                        )
                      })}
                  </div>
                </>}
              {pane === 2 && //--------------------------------------Dimensions pane ---------------------------
                (() => {
                  const twoFilterEntries = Object.entries(selectedDisplayItem?.twofilter || {})
                    .sort((a: any, b: any) => a[0] < b[0] ? 1 : -1);
                  // console.log('Debug twoFilterEntries: ', twoFilterEntries);

                  // Extract groups
                  const ymGroups = twoFilterEntries.filter(([key]) => key.startsWith("ym="));
                  // console.log('Debug ymGroups: ', ymGroups);
                  const yGroups = twoFilterEntries.filter(([key]) => key.startsWith("y="));
                  // console.log('Debug yGroups: ', yGroups);
                  const otherGroups = twoFilterEntries.filter(([key]) => !key.startsWith("ym=") && !key.startsWith("y="));
                  // console.log('Debug otherGroups: ', otherGroups);

                  // Calculate options
                  const ymOptions = ymGroups.map(([key]) => key.replace("ym=", "")).sort().reverse();
                  const yOptions = yGroups.map(([key]) => key.replace("y=", "")).sort().reverse();

                  const currentYM = (selectedDimYM && ymOptions.includes(selectedDimYM)) ? selectedDimYM : ymOptions[0];
                  const currentY = (selectedDimY && yOptions.includes(selectedDimY)) ? selectedDimY : yOptions[0];

                  return (
                    <>
                      {/* Section 1: Year-Month Dropdown */}
                      {ymGroups.length > 0 && (
                        <div className="cpd-section" style={{ marginBottom: "12px" }}>
                          <div className="cpd-filter-bar" style={{ marginBottom: "1rem" }}>
                            <span style={{ marginRight: "0.5rem", fontWeight: 500 }}>Year-Month:</span>
                            <select
                              className="cpd-select"
                              value={currentYM || ""}
                              onChange={(e) => setSelectedDimYM(e.target.value)}
                            >
                              {ymOptions.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                            </select>
                          </div>
                          {ymGroups
                            .filter(([key]) => key === `ym=${currentYM}`)
                            .map(([key, items]: any, entryIndex: number) => (
                              <div key={entryIndex}>
                                {/* <div>{key}</div> */}
                                <div className="cpd-horizontal-scroll" onScroll={updateImage}>
                                  {items
                                    .filter((item: any) => (
                                      search ? item.filename.toLowerCase().includes(search.toLowerCase())
                                        : true))
                                    .sort((a: any, b: any) => {
                                      return a.filename.split(".")?.[0] < b.filename.split(".")?.[0] ? -1 : 1
                                    })
                                    .slice(0, 25)
                                    .map((item: any, itemIndex: number) => (
                                      <PreviewPic
                                        key={itemIndex}
                                        setState={props.setState}
                                        selectedTab={selectedTab}
                                        datasetId={props.datasetId}
                                        previewItem={item}
                                        initialLoad={initialLoad}
                                        updateImage={updateImage}
                                        imageUrl={imageUrl}
                                        imageRef={imageRef}
                                        index={item.originalIndex}
                                        toUrl={toUrl}
                                        urlPrefix={props.urlPrefix}
                                      />
                                    ))}
                                </div>
                              </div>
                            ))}
                        </div>
                      )}

                      {/* Section 2: Year Dropdown */}
                      {yGroups.length > 0 && (
                        <div className="cpd-section" style={{ marginBottom: "12px" }}>
                          <div className="cpd-filter-bar" style={{ marginBottom: "1rem" }}>
                            <span style={{ marginRight: "0.5rem", fontWeight: 500 }}>Year:</span>
                            <select
                              className="cpd-select"
                              value={currentY || ""}
                              onChange={(e) => setSelectedDimY(e.target.value)}
                            >
                              {yOptions.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                            </select>
                          </div>
                          {yGroups
                            .filter(([key]) => key === `y=${currentY}`)
                            .map(([key, items]: any, entryIndex: number) => (
                              <div key={entryIndex}>
                                {/* <div>{key}</div> */}
                                <div className="cpd-horizontal-scroll" onScroll={updateImage}>
                                  {items
                                    .filter((item: any) => (
                                      search ? item.filename.toLowerCase().includes(search.toLowerCase())
                                        : true))
                                    .sort((a: any, b: any) => {
                                      return a.filename.split(".")?.[0] < b.filename.split(".")?.[0] ? -1 : 1
                                    })
                                    .slice(0, 25)
                                    .map((item: any, itemIndex: number) => (
                                      <PreviewPic
                                        key={itemIndex}
                                        setState={props.setState}
                                        selectedTab={selectedTab}
                                        datasetId={props.datasetId}
                                        previewItem={item}
                                        initialLoad={initialLoad}
                                        updateImage={updateImage}
                                        imageUrl={imageUrl}
                                        imageRef={imageRef}
                                        index={item.originalIndex}
                                        toUrl={toUrl}
                                        urlPrefix={props.urlPrefix}
                                      />
                                    ))}
                                </div>
                              </div>
                            ))}
                        </div>
                      )}

                      {/* Section 3: Other Dimensions (Persist) */}
                      <div>
                        {otherGroups
                          .map(([key, items]: any) => ([
                            key,
                            items
                              .filter((item: any) => (Object.keys(item?.filter)?.length === 1
                                && !["y", "ym", "ymd"].includes(item?.parts?.[0]?.split("__")?.[0])))
                              .filter((item: any) => (
                                search ? item.filename.toLowerCase().includes(search.toLocaleLowerCase())
                                  : true))
                          ]))
                          .filter((entry: any) => entry[1].length > 0)
                          .map((entry: any, entryIndex: number) => {
                            return (
                              <div key={entryIndex}>
                                <div>{entry[0]}</div>
                                <div className="cpd-horizontal-scroll" onScroll={updateImage}>
                                  {entry[1]
                                    .sort((a: any, b: any) => {
                                      return a.filename.split(".")?.[0] < b.filename.split(".")?.[0] ? -1 : 1
                                    })
                                    .slice(0, 25)
                                    .map((item: any, itemIndex: number) => {
                                      return (
                                        <PreviewPic
                                          key={itemIndex}
                                          setState={props.setState}
                                          selectedTab={selectedTab}
                                          datasetId={props.datasetId}
                                          previewItem={item}
                                          initialLoad={initialLoad}
                                          updateImage={updateImage}
                                          imageUrl={imageUrl}
                                          imageRef={imageRef}
                                          index={item.originalIndex}
                                          toUrl={toUrl}
                                          urlPrefix={props.urlPrefix}
                                        />
                                      )
                                    })}
                                </div>
                              </div>
                            )
                          })}
                      </div>

                      {/* Footer 1 */}
                      <div className="cpd-flex-wrap">
                        {(selectedDisplayItem?.one || [])
                          .filter((item: any) => !["y", "ym", "ymd"].includes(item?.parts?.[0]?.split("__")?.[0]))
                          .filter((item: any) => (
                            search ? item.filename.toLowerCase().includes(search.toLocaleLowerCase())
                              : true
                          ))
                          .map((item: any, childIndex: number) => (
                            <PreviewPic
                              key={childIndex}
                              setState={props.setState}
                              selectedTab={selectedTab}
                              datasetId={props.datasetId}
                              previewItem={item}
                              initialLoad={initialLoad}
                              updateImage={updateImage}
                              imageUrl={imageUrl}
                              imageRef={imageRef}
                              index={item.originalIndex}
                              toUrl={toUrl}
                              urlPrefix={props.urlPrefix}
                            />
                          ))}
                      </div>

                      {/* Footer 2 */}
                      <div className="cpd-flex-wrap">
                        {(selectedDisplayItem.two || [])
                          .filter((item: any) => !["y", "ym", "ymd"].includes(item?.parts?.[0]?.split("__")?.[0]))
                          .filter((item: any) => (
                            search ? item.filename.toLowerCase().includes(search.toLocaleLowerCase())
                              : true
                          ))
                          .map((item: any, childIndex: number) => (
                            <PreviewPic
                              key={childIndex}
                              setState={props.setState}
                              selectedTab={selectedTab}
                              datasetId={props.datasetId}
                              previewItem={item}
                              initialLoad={initialLoad}
                              updateImage={updateImage}
                              imageUrl={imageUrl}
                              imageRef={imageRef}
                              index={item.originalIndex}
                              toUrl={toUrl}
                              urlPrefix={props.urlPrefix}
                            />
                          ))}
                      </div>
                    </>
                  );
                })()
              }
              {pane === 3 && //--------------------------------------Custom pane ---------------------------
                <>
                  <div className="cpd-filter-bar">
                    <select
                      className="cpd-select"
                      value={selectedFilters.year || ""}
                      onChange={(e) => setSelectedFilters({ ...selectedFilters, year: e.target.value })}
                    >
                      <option value="" disabled>Select Year</option>
                      {filterOptions.year.map(year => (
                        <option key={year} value={year}>{year}</option>
                      ))}
                    </select>

                    <div className="cpd-select-wrapper">
                      <select
                        className="cpd-select"
                        value={selectedFilters.month || ""}
                        onChange={(e) => setSelectedFilters({ ...selectedFilters, month: e.target.value })}
                      >
                        <option value="" disabled>Select Month</option>
                        {filterOptions.month.map(month => (
                          <option key={month} value={month}>{moment(month, 'MM').format('MMMM')}</option>
                        ))}
                      </select>
                      {selectedFilters.month && (
                        <button
                          className="cpd-remove-filter"
                          onClick={() => setSelectedFilters({ ...selectedFilters, month: "" })}
                        >
                          &times;
                        </button>
                      )}
                    </div>

                    {Object.keys(dimensionOptions).map(dim => (
                      <div className="cpd-select-wrapper" key={dim}>
                        <select
                          className="cpd-select"
                          style={{ minWidth: "200px" }}
                          value={selectedDimensionFilters[dim] || ""}
                          onChange={(e) => setSelectedDimensionFilters({ ...selectedDimensionFilters, [dim]: e.target.value })}
                        >
                          <option value="" disabled>
                            {`Select ${dim.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}`}
                          </option>
                          {dimensionOptions[dim].map(value => (
                            <option key={value} value={value}>{value}</option>
                          ))}
                        </select>
                        {selectedDimensionFilters[dim] && (
                          <button
                            className="cpd-remove-filter"
                            onClick={() => setSelectedDimensionFilters({ ...selectedDimensionFilters, [dim]: "" })}
                          >
                            &times;
                          </button>
                        )}
                      </div>
                    ))}
                    <button
                      className="cpd-btn cpd-btn-primary"
                      onClick={() => {
                        filterItems(customItems, selectedFilters, selectedDimensionFilters)
                      }}
                    >
                      Filter
                    </button>
                    <label className="cpd-update-label">
                      Latest update: {props.chartInfo[props.datasetId].latestUpdated
                        ? moment(props.chartInfo[props.datasetId].latestUpdated).format("DD/MM/YYYY [[]HH:mm[]]")
                        : "N/A"}
                    </label>
                  </div>
                  <div className="cpd-grid">
                    {filteredItems.length > 0 ? (
                      filteredItems.slice(0, 25).map((item: any) => (
                        <div key={`${getPrefix(props.datasetId)}:${item.filename}`}>
                          <CustomChart
                            filename={item.filename}
                            vegaSpec={item.vega_spec}
                            datasetId={props.datasetId}
                            prefix={getPrefix(props.datasetId)}
                            onEvent={props.onEvent}
                          />
                        </div>
                      ))
                    ) : (
                      <div className="cpd-empty-state">
                        No Data Found
                      </div>
                    )}
                  </div>
                </>
              }
              {pane === 4 && //--------------------------------------Dashboard pane ---------------------------
                <GaDashboard
                  datasetId={datasetId}
                  chartInfo={props.chartInfo}
                  onEvent={props.onEvent}
                  duckLoaded={props.duckLoaded}
                />
              }
            </div>
            : !datasetId ?
              <div className="cpd-no-dataset">
                <div className="cpd-icon-circle">
                  <BarChart3 size={48} className="text-gray-300" />
                </div>
                <h3 className="cpd-title">No Dataset Selected</h3>
                <p className="cpd-subtitle">Select an item from the sidebar to view details</p>
                {/* escape hatch: sidebar ว่างเพราะโหลดรายการพลาด -> กดสั่งโหลดใหม่ได้โดยไม่ต้อง reload หน้า */}
                <button
                  type="button"
                  className="cpd-reload-dataset"
                  onClick={() => {
                    props.onEvent({ message: "GetDatasetList", params: {} })
                  }}
                >
                  Reload dataset list
                </button>
              </div>
              : null
          }
        </div>
      </div>
      <Dialog
        open={showChart}
        onOpenChange={(flag: boolean) => {
          if (!flag) {
            props.setState({ dimList: [], filter: null, colorValues: [], showChart: false });
          }
        }}>
        <DialogContent
          aria-describedby={undefined}
          className="ischart-dialog-content--large"
          style={{ background: "var(--background, oklch(1 0 0))" }}
          onOpenAutoFocus={(e: any) => e.preventDefault()}
        >
          <DialogTitle>Chart</DialogTitle>
          <Chart
            width={props.width}
            height={props.height}
            onEvent={props.onEvent}
            setState={props.setState}
            datasetId={props.datasetId}
            prefix={getPrefix(props.datasetId)}
            showChart={showChart}
            filename={props.filename}
            agg={props.agg}
            selectedTab={props.selectedTab}
            filter={props.filter}
            colorValues={props.colorValues}
            loggedin={props.loggedin}
            dimList={props.dimList}
            duckLoaded={props.duckLoaded}
            urlPrefix={props.urlPrefix}
            previewItems={previewItems}
          />
        </DialogContent>
      </Dialog>
      <Dialog
        open={openAdmin}
        onOpenChange={setOpenAdmin}
      >
        <DialogContent
          aria-describedby={undefined}
          className="ischart-dialog-content--medium"
        >
          <DialogTitle className="ischart-sr-only">Admin</DialogTitle>
          {props.Admin && <Admin
            datasetId={datasetId}
            socketMessage={props.socketMessage}
            clearSocketMessage={props.clearSocketMessage}
          />}
        </DialogContent>
      </Dialog>
    </div>
  )
}

const PreviewPic = (props: any) => {
  // console.log('PreviewPic props: ', props);
  // console.log('PreviewPic props.previewItem.filename: ', props.previewItem.filename);
  let { previewItem, initialLoad, updateImage, selectedTab,
    imageUrl, imageRef, index, toUrl } = props;
  let agg = previewItem.filename?.split('.')?.[1];
  // console.log('PreviewPic agg: ', agg);
  let filter = Object.entries(previewItem.filter || {})
    .map((entry: any) => `${entry[0]}__${entry[1]}`).join('__');
  // console.log('PreviewPic filter: ', filter);

  let query = {
    datasetId: props.datasetId,
    dimList: previewItem.filename?.split(".")?.[0]?.split("__"),
    agg: agg,
    selectedTab: selectedTab,
    filename: previewItem.filename,
    ...(filter ? { filter: filter } : {}),
    ...(previewItem.colorValues ? { colorValues: previewItem.colorValues } : {}),
  }
  // console.log('PreviewPic query: ', query);

  return (
    <div
      style={{
        margin: "2px", width: "160px", height: "160px", cursor: "pointer",
        border: "solid grey 1px", borderRadius: "5px",
        padding: "2px"
      }}>
      <div
        onClick={() => {
          props.setState({ ...query, showChart: true });
        }}
      >
        <div
          style={{
            display: "flex", flexDirection: "column", height: "100%",
            justifyContent: "space-between", alignItems: "center", overflow: 'hidden'
          }}>
          <img
            onLoad={() => {
              if (!initialLoad.current) {
                initialLoad.current = true;
                updateImage();
              }
            }}
            ref={(el: any) => {
              imageRef.current[index] = el;
            }}
            style={{ height: "90px", width: "auto" }}
            src={imageUrl.current?.[toUrl(previewItem.filename)]
              || `${props.urlPrefix}/blankscreen.png`}
          />
          <div style={{ overflow: "hidden", fontSize: ".7rem" }}>
            {
              previewItem.displayName ?
                <>
                  <div style={{ textAlign: "center", whiteSpace: "nowrap" }}>
                    {previewItem.displayName.split("__")?.[0]}
                  </div>
                  <div style={{ textAlign: "center" }}>
                    {previewItem.displayName.split("__")?.[1]}
                  </div>
                  <div style={{ textAlign: "center" }}>
                    {previewItem.displayName.split("__")?.[2]}
                  </div>
                  <div style={{ textAlign: "center" }}>
                    {previewItem.displayName.split("__")?.[3]}
                  </div>
                </>
                :
                <>
                  <div style={{ textAlign: "center" }}>
                    {previewItem.filename?.split(".")?.[1]}
                  </div>
                  <div style={{ textAlign: "center" }}>
                    {previewItem.filename?.split(".")?.[0]?.split('__').join(", ")}
                  </div>
                  <div style={{ textAlign: "center" }}>
                    {previewItem.filename?.split(".")?.[2]?.split("__")
                      .reduce((acc: string, cur: string, index: number) =>
                        index % 2 === 1 ? acc + " " + cur : acc,
                        "")}
                  </div>
                </>
            }
          </div>
        </div>
      </div>
    </div>)
}
